import React, { useEffect, useRef, useState } from "react";
import api from "../../core/api/config";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import EditUserModal from "./components/EditUserModal";
import { Select, Input } from "antd";
import CreateUserModal from "./components/CreateUserModal";
import { useSelector } from "react-redux";

const Users = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [loading, setLoading] = useState(false);
  const selectAllRef = useRef(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [usuarioLogado, setUsuarioLogado] = useState(null);

  const { acessoAtivo } = useSelector((state) => state.access);

  const MySwal = withReactContent(Swal);

  const [colunaFiltro, setColunaFiltro] = useState("nome");
  const [valorFiltro, setValorFiltro] = useState("");

  /* ===============================
     CARREGAR USUÁRIO LOGADO
  =============================== */
  const carregarUsuarioLogado = async () => {
    try {
      const { data } = await api.get("/api/user/show");
      setUsuarioLogado(data.user);
    } catch (error) {
      console.error(error);
      MySwal.fire(
        "Erro",
        "Não foi possível identificar o usuário logado",
        "error",
      );
    }
  };

  /* ===============================
     LISTAR USUÁRIOS
  =============================== */
  const listarUsuarios = async () => {
    if (!usuarioLogado?.id || !acessoAtivo?.cidade) return;
    console.log("Carregando usuários para", usuarioLogado.id, acessoAtivo.cidade);

    try {
      setLoading(true);

      const { data } = await api.get(
        `/api/tracking/usuario/permissao/${usuarioLogado.id}/${acessoAtivo.cidade}`,
      );

      const formatados = (data || []).map((u) => ({
        id: u.id,
        nome: u.name,
        email: u.email,
        estabelecimento: u.estabelecimento || "",
        ativo: u.status,
        cpf: u.cpf,
        tipo: u.tipo,
        tipoDescricao: u.tipoDescricao,
        ibge: u.ibge ?? null,
        municipio: u.municipio,
      }));

      setUsuarios(formatados);
    } catch (error) {
      console.error(error);
      MySwal.fire("Erro", "Falha ao carregar usuários", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ===============================
     EFFECTS
  =============================== */
  useEffect(() => {
    carregarUsuarioLogado();
  }, []);

  useEffect(() => {
    if (usuarioLogado?.id && acessoAtivo?.cidade) {
      listarUsuarios();
    }
  }, [usuarioLogado, acessoAtivo]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      selecionados.length > 0 && selecionados.length < usuarios.length;
  }, [selecionados, usuarios]);

  const toggleSelecionado = (id) => {
    setSelecionados((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const selecionarTodos = () => {
    setSelecionados(
      selecionados.length === usuarios.length ? [] : usuarios.map((u) => u.id),
    );
  };

  const usuariosFiltrados = usuarios.filter((u) => {
    if (!valorFiltro) return true;
    const valor = (u[colunaFiltro] ?? "").toString().toLowerCase();
    return valor.includes(valorFiltro.toLowerCase());
  });

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="page-title">
            <h4>Gerenciar Usuários</h4>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="d-flex gap-2 mb-3">
              <Select
                value={colunaFiltro}
                style={{ width: 180 }}
                onChange={(v) => {
                  setColunaFiltro(v);
                  setValorFiltro("");
                }}
                options={[
                  { value: "nome", label: "Nome" },
                  { value: "cpf", label: "CPF" },
                  { value: "email", label: "Email" },
                  { value: "ativo", label: "Status" },
                ]}
              />

              {colunaFiltro === "ativo" ? (
                <Select
                  style={{ width: 160 }}
                  placeholder="Selecione"
                  value={valorFiltro || undefined}
                  onChange={(v) => setValorFiltro(v)}
                  allowClear
                  options={[
                    { value: "true", label: "Ativo" },
                    { value: "false", label: "Inativo" },
                  ]}
                />
              ) : (
                <Input
                  placeholder={`Buscar por ${colunaFiltro}`}
                  value={valorFiltro}
                  onChange={(e) => setValorFiltro(e.target.value)}
                  allowClear
                />
              )}
            </div>

            <div className="table-responsive">
              <table className="table table-hover table-bordered align-middle">
                <thead className="thead-light">
                  <tr>
                    <th width="40">
                      <input
                        type="checkbox"
                        ref={selectAllRef}
                        checked={
                          usuarios.length > 0 &&
                          selecionados.length === usuarios.length
                        }
                        onChange={selecionarTodos}
                      />
                    </th>
                    <th>Nome</th>
                    <th>CPF</th>
                    <th>Email</th>
                    <th>Município</th>
                    <th>Status</th>
                    <th width="40"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="7" className="text-center">
                        Carregando usuários...
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    usuariosFiltrados.map((usuario) => (
                      <tr key={usuario.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selecionados.includes(usuario.id)}
                            onChange={() => toggleSelecionado(usuario.id)}
                          />
                        </td>
                        <td>{usuario.nome}</td>
                        <td>{usuario.cpf}</td>
                        <td>{usuario.email}</td>
                        <td>{usuario.municipio || "-"}</td>
                        <td>
                          <span
                            className={`badge ${
                              usuario.ativo
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                            }`}
                          >
                            {usuario.ativo ? "Ativo" : "Inativo"}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => setUsuarioEditando(usuario)}
                          >
                            <i className="fas fa-edit" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <CreateUserModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={listarUsuarios}
      />

      {usuarioEditando && (
        <EditUserModal
          key={usuarioEditando.id}
          show={true}
          usuario={usuarioEditando}
          onClose={() => setUsuarioEditando(null)}
          onUpdated={listarUsuarios}
        />
      )}
    </div>
  );
};

export default Users;
